import React, { useState, useRef, useEffect } from 'react';
import EquipmentInfoGalleryModal, { buildImageList, PLACEHOLDER } from './EquipmentInfoGalleryModal';

function Chip({ variant = 'yellow', children }) {
  const styles = {
    yellow: {
      background: '#fff6d6',
      color: '#7a5b00',
      border: '1px solid #ffe08a',
    },
    green: {
      background: '#f0fdf4',
      color: '#166534',
      border: '1px solid #bbf7d0',
    }
  };

  const style = styles[variant] || styles.yellow;

  return (
    <span
      style={{
        ...style,
        borderRadius: '999px',
        padding: '2px 8px',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export default function EquipmentInfo({ detail }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);
  const detailsMeasureRef = useRef(null);
  const [detailsTruncated, setDetailsTruncated] = useState(false);

  const imageUrls = detail ? buildImageList(detail) : [];
  const coverSrc = imageUrls[0] || PLACEHOLDER;
  const photoCount = imageUrls.length;

  useEffect(() => {
    const el = detailsMeasureRef.current;
    const text = detail?.details?.trim();
    if (!el || !text) {
      setDetailsTruncated(false);
      return;
    }
    const measure = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setDetailsTruncated(el.scrollWidth > el.clientWidth + 1);
        });
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [detail?.details]);

  const openModal = (imageIndex = 0) => {
    setModalInitialIndex(imageIndex);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  if (!detail) return null;
  
  // Check if there are any ongoing issues
  const hasOngoingIssues = (detail.issues || []).some(issue => issue.status === 'Ongoing');
  const statusText = hasOngoingIssues ? 'Needs Service' : 'Healthy';
  const statusVariant = hasOngoingIssues ? 'yellow' : 'green';
  const detailsText = detail.details?.trim() ? detail.details : '—';
  
  return (
    <>
    <div className="bg-white rounded-lg p-3 mechanical-equipment-info" style={{ borderRadius: 12, flex: '1.2', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: '240px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => openModal(0)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openModal(0);
            }
          }}
          style={{
            flex: '0 1 auto',
            minWidth: '120px',
            maxWidth: '280px',
            height: 200,
            overflow: 'hidden',
            borderRadius: '8px',
            padding: '16px',
            cursor: 'pointer',
            position: 'relative',
          }}
          title={photoCount > 1 ? `View ${photoCount} photos` : 'View photo'}
        >
          <img 
            src={coverSrc}
            alt={detail.name} 
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
            onError={(e) => {
              e.target.src = PLACEHOLDER;
            }}
          />
          {photoCount > 1 && (
            <div
              style={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                pointerEvents: 'none',
                background: '#fff6d6',
                color: '#7a5b00',
                border: '1px solid #ffe08a',
                borderRadius: '999px',
                padding: '2px 8px',
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              +{photoCount - 1} more
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h4 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail.name}</h4>
            <Chip variant={statusVariant}>{statusText}</Chip>
          </div>
          <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 6, columnGap: 16, fontSize: 14 }}>
            <div style={{ color: '#1f2937', fontWeight: 500 }}>Location</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>{detail.location}</div>
            <div style={{ color: '#1f2937', fontWeight: 500 }}>Model</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>{detail.manufacturer} {detail.model}</div>
            <div style={{ color: '#1f2937', fontWeight: 500 }}>Date</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>{detail.year}</div>
            <div style={{ color: '#1f2937', fontWeight: 500 }}>Quantity</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>{detail.quantity ?? '—'}</div>
            <div style={{ color: '#1f2937', fontWeight: 500, alignSelf: 'center' }}>Details</div>
            <div
              style={{
                minWidth: 0,
                color: '#6b7280',
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                ref={detailsMeasureRef}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {detailsText}
              </span>
              {detailsTruncated && detailsText !== '—' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(0);
                  }}
                  style={{
                    flexShrink: 0,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: '#2563eb',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    whiteSpace: 'nowrap',
                  }}
                >
                  See more
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <EquipmentInfoGalleryModal
      open={modalOpen}
      onClose={closeModal}
      detail={detail}
      initialImageIndex={modalInitialIndex}
    />
    </>
  );
}
